import { Theme } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";

declare module "@mui/material" {
  interface DefaultTheme extends Theme {}
}

declare module "@mui/material/styles" {
  interface Palette {
    text: {
      primary: string;
      secondary: string;
      brand: string;
      disabled: string;
      "system-green": string;
      "system-red": string;
      "btn-dark": string;
    };
    // Background
    brand: {
      primary: string;
      "primary-bg": string;
    };
    system: {
      green: string;
      "green-bg": string;
      "green-alarm": string;
      red: string;
      "red-bg": string;
      "red-alarm": string;
    };
    border: {
      dark: string;
      light: string;
      white: string;
    };
    surface: {
      light: string;
      "light-bg": string;
      silver: string;
      container: string;
      bg: string;
      "container-bg": string;
      black: string;
    };
  }

  interface PaletteOptions {
    text?: {
      primary?: string;
      secondary?: string;
      brand?: string;
      disabled?: string;
      "system-green"?: string;
      "system-red"?: string;
      "btn-dark"?: string;
    };
    // Background
    brand?: {
      primary?: string;
      "primary-bg"?: string;
    };
    system?: {
      green?: string;
      "green-bg"?: string;
      "green-alarm"?: string;
      red?: string;
      "red-bg"?: string;
      "red-alarm"?: string;
    };
    border?: {
      dark?: string;
      light?: string;
      white?: string;
    };
    surface?: {
      light?: string;
      "light-bg"?: string;
      silver?: string;
      container?: string;
      bg?: string;
      "container-bg"?: string;
      black?: string;
    };
  }

  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    "token-call-joined-sm": true;
    "token-call-joined-md": true;
    "token-call-trending-sm": true;
    "token-call-trending-md": true;
    "cabal-list-sm": true;
  }
}

// Colors
const light = {
  100: "#FFFFFF",
  200: "#CCCCCC",
  300: "#D4D4D4",
};

const dark = {
  100: "#595E65",
  200: "#373C44",
  400: "#24272C",
  500: "#212327",
  600: "#0B0B0B",
};

const orange = {
  300: "#FFC949",
  500: "#AE7A00",
  700: "#6D4D02",
};

const green = {
  100: "#3F0",
  300: "#D9FCA2",
  500: "#26894F",
  700: "#0D3F22",
};

const red = {
  100: "#FF003C",
  300: "#FCACA2",
  500: "#893526",
  700: "#3F150D",
};

// A custom theme for this app
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      "token-call-joined-sm": 400,
      "token-call-joined-md": 450,
      "token-call-trending-sm": 360,
      "token-call-trending-md": 410,
      "cabal-list-sm": 450,
    },
  },
  palette: {
    // TODO: Old theme to be removed
    primary: {
      main: "#FFB800",
      contrastText: "#000",
    },
    secondary: {
      main: "#313855",
    },
    error: {
      main: "#ef5350",
    },
    warning: {
      main: "#FF5147",
    },
    action: {
      active: "#ffffff",
      hover: "#00000",
      disabledBackground: "#B2B2B2",
    },
    // Text
    text: {
      primary: light[100],
      secondary: light[300],
      brand: orange[700],
      disabled: dark[100],
      "system-green": green[300],
      "system-red": red[300],
      "btn-dark": dark[600],
    },
    // Background
    brand: {
      primary: orange[300],
      "primary-bg": orange[700],
    },
    system: {
      green: green[500],
      "green-bg": green[700],
      "green-alarm": green[100],
      red: red[500],
      "red-bg": red[700],
      "red-alarm": red[100],
    },
    border: {
      dark: dark[600],
      light: light[300],
      white: light[100],
    },
    surface: {
      light: light[100],
      "light-bg": light[200],
      silver: dark[100],
      container: dark[200],
      bg: dark[400],
      "container-bg": dark[500],
      black: dark[600],
    },
  },
  typography: {
    // Old typography to be removed
    fontFamily: "Cabin Condensed, sans-serif",
    button: {
      textTransform: "none",
    },
    // Custom typography
    "display-lg": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "64px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    "display-md": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "56px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    "display-sm": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "48px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    "title-h1": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "40px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    "title-h2": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "32px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    "title-h3": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "24px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    subtitle: {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "20px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "-0.02em",
    },
    "body-lg": {
      fontFamily: "Sora Font, sans-serif",
      fontSize: "20px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "body-md": {
      fontFamily: "Sora Font, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "body-default": {
      fontFamily: "Sora Font, sans-serif",
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "body-sm": {
      fontFamily: "Sora Font, sans-serif",
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "pre-title": {
      fontFamily: "Sora Font, sans-serif",
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "0.05em",
    },
    "pre-title-2": {
      fontFamily: "Sora Font, sans-serif",
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "1.4em",
      letterSpacing: "0.05em",
    },
    "button-lg": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "18px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "button-md": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "button-sm": {
      fontFamily: "Edit Undo BRK, sans-serif",
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "1.4em",
    },
    "mobile-display-m": {
      fontFamily: "Instrument Serif, sans-serif",
      fontSize: "50px",
      fontWeight: "400",
      lineHeight: "50px",
      letterSpacing: "0",
    },
    "mobile-display-s": {
      fontFamily: "Instrument Serif, sans-serif",
      fontSize: "32px",
      fontWeight: "400",
      lineHeight: "32px",
      letterSpacing: "0",
    },
    "mobile-display-xs": {
      fontFamily: "Instrument Serif, sans-serif",
      fontSize: "26px",
      fontWeight: "400",
      lineHeight: "26px",
      letterSpacing: "0",
    },
    "mobile-h1": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "40px",
      fontWeight: "500",
      lineHeight: "40px",
      letterSpacing: "-0.03em",
    },
    "mobile-h2": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "32px",
      fontWeight: "500",
      lineHeight: "34px",
      letterSpacing: "-0.05em",
    },
    "mobile-h3": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "24px",
      fontWeight: "500",
      lineHeight: "24px",
      letterSpacing: "-0.02em",
    },
    "mobile-body-m": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "18px",
      fontWeight: "400",
      lineHeight: "20px",
      letterSpacing: "-0.03em",
    },
    "mobile-body-s": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "15px",
      fontWeight: "400",
      lineHeight: "16px",
      letterSpacing: "-0.03em",
    },
    "mobile-body-xs": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "14px",
      letterSpacing: "0",
    },
    "mobile-body-m-heavy": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "18px",
      fontWeight: "500",
      lineHeight: "20px",
      letterSpacing: "-0.03em",
    },
    "mobile-body-s-heavy": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "15px",
      fontWeight: "500",
      lineHeight: "16px",
      letterSpacing: "-0.03em",
    },
    "mobile-action-s": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "15px",
      fontWeight: "400",
      lineHeight: "16px",
      letterSpacing: "-0.03em",
    },
    "mobile-action-s-heavy": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "15px",
      fontWeight: "600",
      lineHeight: "16px",
      letterSpacing: "0",
    },
    "mobile-action-xs-heavy": {
      fontFamily: "Instrument San, sans-serif",
      fontSize: "12px",
      fontWeight: "600",
      lineHeight: "14px",
      letterSpacing: "0",
    },
  },
});

export default theme;
